import {type AxiosRequestConfig} from 'axios';

import {spotheroGet} from 'api';
import {type ApiResponse} from 'api/api.types';
import {apiDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type CompPassesResponse,
    type CompPassesResults,
    type GetCompPassesParams,
} from 'api/comp-passes/comp-pass.types';

const formatRequestParams = (params: GetCompPassesParams) => {
    const updatedRequest = {
        ...params,
        fromDate: dayjs(params.fromDate).format(apiDateFormat),
        toDate: dayjs(params.toDate).format(apiDateFormat),
    };

    const formattedRequest = convertKeysToSnakeCase(updatedRequest);

    return formattedRequest;
};

const formatResponse = (
    response: ApiResponse<CompPassesResponse>
): CompPassesResults => {
    const formattedData: CompPassesResults = camelizeKeys(response.data);

    return formattedData;
};

/**
 * Gets comp passes
 *
 * @param facilityId - The id of the facility to get the comp passes for
 * @param params - The id of the comp pass to delete
 * @param config - Additional Axios configuration options for the request.
 * @param config.signal - An abort signal to cancel the request.
 *
 * @see '/api/v1/facilities/{facilityId}/comp-pass/passes/'
 */
export const getCompPasses = async (
    facilityId: number,
    params: GetCompPassesParams,
    config: AxiosRequestConfig = {}
): Promise<CompPassesResults> => {
    try {
        const formattedParams = formatRequestParams(params);

        const response = await spotheroGet<CompPassesResponse>(
            `facilities/${facilityId}/comp-pass/passes/`,
            formattedParams,
            {
                paramsSerializer: {
                    indexes: null,
                },
                ...config,
            }
        );

        const formattedResponse = formatResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};
